import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const nameSchema = z.string().trim().min(1).max(140)
const accountNameSchema = z.string().trim().min(1).max(140)
const dateSchema = z.iso.date()

export const financeAccountMappingsV1Schema = z.object({
  receivableAccount: accountNameSchema,
  payableAccount: accountNameSchema,
  incomeAccount: accountNameSchema,
  expenseAccount: accountNameSchema,
  roundOffAccount: accountNameSchema,
}).strict()

export const branchAccountingMappingV1Schema = z.object({
  branchId: branchIdSchema,
  costCenter: nameSchema,
  accountingDimension: nameSchema,
}).strict()

export const financeConfigurationApplyV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
  configurationId: entityIdSchema,
  revisionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  company: nameSchema,
  fiscalYear: nameSchema,
  fiscalYearStart: dateSchema,
  fiscalYearEnd: dateSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  accounts: financeAccountMappingsV1Schema,
  branches: z.array(branchAccountingMappingV1Schema).min(1).max(100),
}).strict().superRefine((value, context) => {
  if (value.fiscalYearEnd <= value.fiscalYearStart) {
    context.addIssue({
      code: "custom",
      message: "Fiscal year end must follow its start.",
      path: ["fiscalYearEnd"],
    })
  }
  const branchIds = value.branches.map((branch) => branch.branchId)
  const costCenters = value.branches.map((branch) => branch.costCenter)
  if (new Set(branchIds).size !== branchIds.length) {
    context.addIssue({
      code: "custom",
      message: "Branch mappings must be unique.",
      path: ["branches"],
    })
  }
  if (new Set(costCenters).size !== costCenters.length) {
    context.addIssue({
      code: "custom",
      message: "Cost-center mappings must be unique.",
      path: ["branches"],
    })
  }
})

export const financeConfigurationResultV1Schema = z.object({
  entityType: z.literal("finance_configuration"),
  entityId: entityIdSchema,
  state: z.literal("validated"),
  version: z.number().int().positive(),
  revisionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  company: nameSchema,
  fiscalYear: nameSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  branchCount: z.number().int().positive(),
  crossEntityDefaultsRejected: z.literal(true),
}).strict()

export type FinanceConfigurationApplyV1 = z.infer<
  typeof financeConfigurationApplyV1Schema
>
export type FinanceConfigurationResultV1 = z.infer<
  typeof financeConfigurationResultV1Schema
>
